import { useTranslation } from 'react-i18next';
import { ExternalLink, Sparkles } from 'lucide-react';

const DISC_ASSESSMENT_URL = "https://www.thewementality.com/product-page/kids-disc";

/**
 * DISC Assessment Upsell Component
 * Displays optional upsell for the official Kids DISC Personality Assessment ($49)
 * 
 * @param {string} variant - 'lesson' | 'businessPlan' | 'completion'
 * @param {boolean} hasDiscAssessment - Whether user has already taken the assessment
 */
function DISCAssessmentUpsell({ variant = 'lesson', hasDiscAssessment = false }) {
  const { t } = useTranslation();

  // Don't show if user already has assessment
  if (hasDiscAssessment) {
    return null;
  }

  const handleClick = () => {
    window.open(DISC_ASSESSMENT_URL, '_blank', 'noopener,noreferrer');
  };

  // Lesson variant - Primary placement after Session 2, Lesson 2
  if (variant === 'lesson') {
    return (
      <div 
        className="mt-8 p-6 bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-200 rounded-xl shadow-sm"
        data-testid="disc-assessment-upsell-lesson"
      >
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
              <span className="text-2xl">🔍</span>
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-amber-900 mb-2">
              {t('discAssessment.title')}
            </h3>
            <p className="text-amber-800 mb-4">
              {t('discAssessment.description')}
            </p>
            <button
              onClick={handleClick}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg transition-colors shadow-sm"
            >
              <Sparkles className="w-4 h-4" />
              {t('discAssessment.cta')}
              <ExternalLink className="w-4 h-4 ml-1" />
            </button>
            <p className="text-xs text-amber-600 mt-2">
              {t('discAssessment.externalLink')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Business Plan variant - Inline upsell in Team & Roles section
  if (variant === 'businessPlan') {
    return (
      <div 
        className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg"
        data-testid="disc-assessment-upsell-plan"
      >
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xl">🐦</span>
            <p className="text-amber-800 font-medium">
              {t('discAssessment.businessPlanPrompt')}
            </p>
          </div>
          <button
            onClick={handleClick}
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-lg transition-colors"
          >
            {t('discAssessment.businessPlanCta')} – $49
            <ExternalLink className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    );
  }

  // Completion variant - Parent-focused upsell card
  if (variant === 'completion') {
    return (
      <div 
        className="mt-8 p-6 bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 rounded-xl shadow-sm"
        data-testid="disc-assessment-upsell-completion"
      >
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
            <span className="text-3xl">🎯</span>
          </div>
          <h3 className="text-xl font-bold text-purple-900 mb-2">
            {t('discAssessment.completionTitle')}
          </h3>
          <p className="text-purple-700 mb-4 max-w-md mx-auto">
            {t('discAssessment.completionDescription')}
          </p>
          <button
            onClick={handleClick}
            className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors shadow-md"
          >
            <Sparkles className="w-5 h-5" />
            {t('discAssessment.completionCta')}
            <ExternalLink className="w-4 h-4 ml-1" />
          </button>
          <p className="text-xs text-purple-500 mt-3">
            {t('discAssessment.externalLink')}
          </p>
        </div>
      </div>
    );
  }

  return null;
}

export default DISCAssessmentUpsell;
